import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface Message {
  id: number;
  text: string;
  sender: "user" | "support";
  timestamp: Date;
}

export const SupportChat = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionLang, setSessionLang] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const tt = (key: string): string =>
    i18n.getFixedT(sessionLang || i18n.language)(key);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const lang = (i18n.language || "en").slice(0, 2);
      setSessionLang(lang);
      setMessages([
        {
          id: 1,
          text: i18n.getFixedT(lang)("supportChat.welcome"),
          sender: "support",
          timestamp: new Date(),
        },
      ]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (!isOpen && messages.length === 0) setSessionLang(null);
  }, [isOpen, i18n, messages.length]);

  const handleClose = () => {
    setIsOpen(false);
    setMessages([]);
    setSessionLang(null);
  };

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const sendToAI = async (history: Message[]) => {
    const payload = {
      lang: sessionLang || i18n.language || "en",
      messages: history.map((m) => ({
        role: m.sender === "user" ? "user" : "assistant",
        content: m.text,
      })),
    };

    const { data, error } = await supabase.functions.invoke("support-chat", {
      body: payload,
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return (data?.reply as string) || "";
  };

  const handleSendMessage = async (text?: string) => {
    const messageText = (text ?? inputValue).trim();
    if (!messageText || isTyping) return;

    const userMsg: Message = {
      id: Date.now(),
      text: messageText,
      sender: "user",
      timestamp: new Date(),
    };
    const next = [...messages, userMsg];
    setMessages(next);
    setInputValue("");
    setIsTyping(true);

    try {
      const reply = await sendToAI(next);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: reply || tt("supportChat.defaultResponse"),
          sender: "support",
          timestamp: new Date(),
        },
      ]);
    } catch (e: any) {
      console.error("support-chat error", e);
      toast({
        title: "Support",
        description: e?.message || "Connection issue. Please try again.",
        variant: "destructive",
      });
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: tt("supportChat.defaultResponse"),
          sender: "support",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsTyping(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-accent-glow hover:scale-110 transition-transform z-50"
          size="icon"
          aria-label="Open AI Support"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}

      {isOpen && (
        <Card className="fixed bottom-6 right-6 w-[400px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-2rem)] shadow-2xl z-50 bg-card border-2 border-primary/20 flex flex-col backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-t-lg p-4 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary-foreground/20 flex items-center justify-center text-base font-bold">
                V
              </div>
              <div>
                <CardTitle className="text-lg">{tt("supportChat.title")}</CardTitle>
                <Badge variant="secondary" className="text-xs mt-1">{tt("supportChat.alwaysOnline")}</Badge>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="text-primary-foreground hover:bg-primary-foreground/20"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </Button>
          </CardHeader>

          <CardContent className="flex-1 p-0 flex flex-col overflow-hidden">
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg p-3 ${
                        message.sender === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted/80 text-foreground border border-border"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-line leading-relaxed">{message.text}</p>
                      <span className="text-xs opacity-70 mt-1 block">
                        {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-muted/80 rounded-lg p-3 border border-border">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-primary rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={scrollRef} />
              </div>
            </ScrollArea>

            {messages.length <= 1 && (
              <div className="px-4 py-2 border-t border-border bg-muted/30">
                <p className="text-xs text-muted-foreground mb-2">{tt("supportChat.quickQuestions")}</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="text-xs h-7"
                    onClick={() => handleSendMessage(tt("supportChat.whatPlansOffer"))}>
                    {tt("supportChat.plans")}
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs h-7"
                    onClick={() => handleSendMessage(tt("supportChat.howMuchCost"))}>
                    {tt("supportChat.pricing")}
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs h-7"
                    onClick={() => handleSendMessage(tt("supportChat.tellFootball"))}>
                    {tt("supportChat.football")}
                  </Button>
                </div>
              </div>
            )}

            <div className="p-4 border-t border-border bg-background/50">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={tt("supportChat.askAnything")}
                  className="bg-background border-border focus:border-primary"
                  disabled={isTyping}
                />
                <Button
                  onClick={() => handleSendMessage()}
                  size="icon"
                  className="shadow-accent-glow"
                  disabled={isTyping || !inputValue.trim()}
                  aria-label="Send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                {tt("supportChat.poweredBy")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};
